import { gql } from "@apollo/client";
import { Classes } from "@blueprintjs/core";
import { toast } from "sonner";

import { Box, Flex } from "@/components/layout/flexbox";
import { ToggleField } from "@/components/pieces/form/ToggleField";
import { SUBTEXT_COLOR } from "@/css/constants";
import { ProductDetailQuery, useToggleProductEnabledMutation } from "@/graphql";

type CoachProduct = ProductDetailQuery["productById"]["coachProduct"];

export const TOGGLE_PRODUCT_MUTATION = gql`
  mutation ToggleProductEnabled($coachProductId: ID!) {
    toggleProductEnabled(coachProductId: $coachProductId) {
      ok
    }
  }
`;

export function ToggleProductEnabled({
  coachProduct,
}: {
  coachProduct: CoachProduct;
}) {
  const [toggleProductEnabled, { loading }] = useToggleProductEnabledMutation({
    refetchQueries: ["ProductDetail"],
  });

  const toggleEnableStatus = async () => {
    const newEnabledState = !coachProduct.enabled;

    try {
      await toggleProductEnabled({
        variables: {
          coachProductId: coachProduct?.id ?? "",
        },
        optimisticResponse: {
          toggleProductEnabled: {
            ok: true,
          },
        },
        update: (cache) => {
          // Update the cached product data optimistically
          cache.modify({
            id: cache.identify(coachProduct),
            fields: {
              coachEnabled: () => newEnabledState,
            },
          });
        },
      });

      toast.success("Saved");
    } catch {
      toast.error("An error occurred. Please try again.");
    }
  };

  return (
    <Flex justifyContent="space-between" alignItems="center">
      <Box flexGrow={1}>
        <Box fontSize={20} fontWeight={600}>
          Enable Service
        </Box>
        <Box
          fontSize={16}
          className={loading ? Classes.SKELETON : ""}
          color={SUBTEXT_COLOR}
        >
          You are{!coachProduct.enabled && " not"} currently accepting bookings
          for this service
        </Box>
      </Box>
      <Flex justifyContent="right" flexBasis={60} flexShrink={0}>
        {loading ? (
          <Box className={Classes.SKELETON} height={26} width={46} />
        ) : (
          <ToggleField
            checked={coachProduct.enabled ?? true}
            onChange={toggleEnableStatus}
          />
        )}
      </Flex>
    </Flex>
  );
}
